import {useState, useCallback} from 'react';
import {FormattedMessage} from 'react-intl';
import {useMutation, type ApolloCache} from '@apollo/client';
import {BOOKMARK_FRAGMENT, CREATE_BOOKMARK_MUTATION, type CreateBookmarkOutput} from 'graphql/admin/bookmarks';
import {useSelectedLinkContext} from 'contexts/SelectedLinkContext';
import {useSnackbar} from 'hooks';
import {getErrorMessage} from 'utils';
import CreateBookmarkDialog from './CreateBookmarkDialog';
import { Button } from '@/components/ui/atoms/Button/Button';
import { Add } from '@mui/icons-material';

const updateBookmarksCache = (cache: ApolloCache<any>, {data} : {data: CreateBookmarkOutput}) => {
  const createdBookmark = data?.createBookmark?.bookmark;
  if (createdBookmark) cache.modify({
    fields: {
      getBookmarks({bookmarks: existingBookmarksRefs = [], ...getBookmarks}) {
        const createdBookmarkCacheRef = cache.writeFragment({
          data: createdBookmark,
          fragment: BOOKMARK_FRAGMENT
        });
        return {
          ...getBookmarks,
          bookmarks: [...existingBookmarksRefs, createdBookmarkCacheRef]
        }
      }
    }
  });
};

type CreateBookmarkProps = {
  selectedFolderKey?: string;
}

const CreateBookmarkButton = ({selectedFolderKey} : CreateBookmarkProps) : JSX.Element => {
  const [open, setOpen] = useState(false);

  const {successSnackbar} = useSnackbar();

  const {selectedLink: {channelKey: channel}} = useSelectedLinkContext();

  const onCompleted = (data : CreateBookmarkOutput) => {
    const createdBookmark = data?.createBookmark?.bookmark;
    if (createdBookmark) {
      successSnackbar(<FormattedMessage id="admin.bookmarks.create.success" values={{ bookmarkName: createdBookmark.name }} />);
      handleClose();
    }
  }
  
  const [createBookmark, {loading, data, error, reset}] = useMutation<CreateBookmarkOutput>(CREATE_BOOKMARK_MUTATION, { update: updateBookmarksCache, onCompleted });
  
  const handleClick = () => setOpen(true);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const handleCreateBookmark = ({bookmarkName, url}) => createBookmark({variables: {createBookmarkInput: {channel, bookmarkName, url, folderKey: selectedFolderKey}}});

  return <>
    <Button onClick={handleClick}>
      <Add />
      <FormattedMessage id="admin.bookmarks.button.createBookmark" />
    </Button>
    <CreateBookmarkDialog
      errorReason={data?.createBookmark?.errorReason || getErrorMessage(error)}
      open={open}
      onClose={handleClose}
      submitting={loading}
      handleCreateBookmark={handleCreateBookmark}
    />
  </>
}

export default CreateBookmarkButton;